import {memo, ShapeComponent, shapeComponent} from "shared/base"
import React, {useMemo} from "react"
import RenderHtml, {defaultSystemFonts} from "react-native-render-html"
import {StyleSheet} from "react-native"
import {useDefaultStyle} from "@kaspernj/api-maker/build/utils/default-style"

const systemFonts = [...defaultSystemFonts, "DreamChaser", "HankenGrotesk", "Nunito"]

/**
 * @typedef {object} SharedRenderHtmlProps
 * @property {Record<string, Record<string, unknown>>} [classesStyles]
 * @property {unknown} source
 * @property {Record<string, Record<string, unknown>>} [tagsStyles]
 */

/** @typedef {object} SharedRenderHtmlState */

/**
 * @extends {ShapeComponent<SharedRenderHtmlProps, SharedRenderHtmlState>}
 */
class SharedRenderHtml extends ShapeComponent {
  render() {
    const {
      classesStyles: callerClassesStyles,
      source,
      tagsStyles: callerTagsStyles,
      ...restProps
    } = this.props
    const defaultStyle = useDefaultStyle()

    const defaultTextStyle = useMemo(() => {
      if (defaultStyle?.Text) {
        const originalTextStyle = StyleSheet.flatten(defaultStyle.Text)
        const newTextStyle = {}

        for (const key in originalTextStyle) {
          const value = originalTextStyle[key]

          if (value === undefined) {
            continue
          }

          newTextStyle[key] = value
        }

        return newTextStyle
      }
    }, [defaultStyle.Text])

    const baseFontSize = useMemo(() => {
      if (typeof defaultTextStyle?.fontSize == "number") {
        return defaultTextStyle.fontSize
      }

      return 14
    }, [defaultTextStyle?.fontSize])

    const sourceProps = useMemo(
      () => ({html: source}),
      [source]
    )

    const tagsStyles = useMemo(() => {
      const defaultTagsStyles = {
        body: Object.assign(
          {fontFamily: defaultTextStyle?.fontFamily || "HankenGrotesk"},
          defaultTextStyle
        ),
        p: {
          fontSize: baseFontSize * 1.2
        }
      }

      return Object.assign({}, callerTagsStyles, {
        body: Object.assign({}, defaultTagsStyles.body, callerTagsStyles?.body),
        p: Object.assign({}, defaultTagsStyles.p, callerTagsStyles?.p)
      })
    }, [baseFontSize, callerTagsStyles, defaultTextStyle])

    const classesStyles = useMemo(() => Object.assign(
      {
        "ckeditor-heading-1": {fontSize: baseFontSize * 7},
        "ckeditor-heading-2": {fontSize: baseFontSize * 5},
        "ckeditor-heading-3": {fontSize: baseFontSize * 4},
        "ckeditor-heading-4": {fontSize: baseFontSize * 2},
        "ckeditor-heading-5": {fontSize: baseFontSize * 1.8},
        "ckeditor-heading-6": {fontSize: baseFontSize * 1.6},
        "ckeditor-heading-7": {fontSize: baseFontSize * 1.3},
        "ckeditor-heading-8": {fontSize: baseFontSize * 1.1}
      },
      callerClassesStyles
    ), [baseFontSize, callerClassesStyles])

    return (
      <RenderHtml
        allowFontScaling
        classesStyles={classesStyles}
        source={sourceProps}
        systemFonts={systemFonts}
        tagsStyles={tagsStyles}
        {...restProps}
      />
    )
  }
}

export default memo(shapeComponent(SharedRenderHtml))
